export type DormLayer = "wall" | "floor" | "carpet" | "back" | "mid" | "front";

export type DormSit = {
  dx: number;
  dy?: number;
  radius?: number;
  facing?: 1 | -1;
};

export type DormPiece = {
  id: string;
  src: string;
  /** Bottom-center X in native wall pixels (wall tile is 1024 wide). */
  x: number;
  layer: DormLayer;
  /** Anchor; default bottom-center for furniture, top-left for room tiles. */
  anchor?: [number, number];
  /** Extra Y in native pixels (negative = up). */
  y?: number;
  /** Uniform scale multiplier on top of the room scale. */
  scale?: number;
  /** Vertical scale only — carpets squash to a floor ellipse. */
  scaleY?: number;
  sit?: DormSit;
  sits?: DormSit[];
  blendAdd?: boolean;
};

export const dormRoom = {
  wallSrc: "/assets/dorm/room/library_wall.png",
  floorSrc: "/assets/dorm/room/library_floor.png",
  wallSize: 1024,
  floorSize: 256,
  tiles: 2,
  groundPad: 18,
  /** Keep Mondragon inside the visible floor. */
  padX: 90,
};

/** Brick columns are ~130px on each side of a 1024 wall tile. */
const brick = (tile: number, side: "left" | "right") =>
  tile * dormRoom.wallSize + (side === "left" ? 64 : 958);

export const dormPieces: DormPiece[] = [
  {
    id: "fireplace-hearth",
    src: "/assets/dorm/furniture/fireplace_hearth.png",
    // Center brick column (tile seam).
    x: dormRoom.wallSize,
    y: -235,
    layer: "back",
    scale: 0.78,
  },
  {
    id: "fireplace-mantel",
    src: "/assets/dorm/furniture/fireplace_mantel.png",
    x: dormRoom.wallSize,
    // Hearth is 296px * 0.78; overlap the join so the panel sits on the shelf.
    y: -466,
    layer: "back",
    scale: 0.78,
  },
  {
    id: "poster-date",
    src: "/assets/dorm/posters/dating2021.png",
    x: brick(0, "left"),
    y: -375,
    layer: "back",
    scale: 0.62,
  },
  {
    id: "poster-whitev",
    src: "/assets/dorm/posters/white_valentine.png",
    x: brick(1, "right"),
    y: -375,
    layer: "back",
    scale: 0.62,
  },
  {
    id: "settee",
    src: "/assets/dorm/furniture/settee.png",
    x: 700,
    layer: "mid",
    sit: { dx: 4, dy: -78, radius: 108, facing: 1 },
  },
  {
    id: "chaise",
    src: "/assets/dorm/furniture/chaise.png",
    x: 1560,
    layer: "mid",
    sits: [
      { dx: -92, dy: -86, radius: 120, facing: -1 },
      { dx: -8, dy: -84, radius: 100, facing: -1 },
    ],
  },
  {
    id: "coffee-left",
    src: "/assets/dorm/furniture/coffee.png",
    x: 140,
    layer: "mid",
    scale: 0.72,
  },
  {
    id: "coffee-right",
    src: "/assets/dorm/furniture/coffee.png",
    x: dormRoom.wallSize * dormRoom.tiles - 140,
    layer: "mid",
    scale: 0.72,
  },
];

export const dormBubbles = ["(._.)", "(OωO)", "(^_^)", "(>_<)", "♪", "(o_o)", "(~_~)"];

export type DormSitSpot = {
  id: string;
  x: number;
  y: number;
  radius: number;
  facing?: 1 | -1;
};

export function dormSitSpots(): DormSitSpot[] {
  return dormPieces.flatMap((p) => {
    const spots = p.sits ?? (p.sit ? [p.sit] : []);
    return spots.map((s, i) => ({
      id: `${p.id}-${i}`,
      x: p.x + s.dx,
      y: s.dy ?? 0,
      radius: s.radius ?? 110,
      facing: s.facing,
    }));
  });
}

export function dormNativeWidth(): number {
  return dormRoom.wallSize * dormRoom.tiles;
}

export function dormXBounds(): { min: number; max: number } {
  const max = dormNativeWidth();
  return { min: dormRoom.padX, max: max - dormRoom.padX };
}

export function clampDormX(x: number): number {
  const { min, max } = dormXBounds();
  return Math.max(min, Math.min(max, x));
}

export function nearestSit(x: number, extra = 0): DormSitSpot | null {
  let best: DormSitSpot | null = null;
  let bestD = Infinity;
  for (const s of dormSitSpots()) {
    const d = Math.abs(s.x - x);
    if (d <= s.radius + extra && d < bestD) {
      best = s;
      bestD = d;
    }
  }
  return best;
}
